import { useEffect, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useCurrentUser } from '../hooks/use-current-user'
import { apiClient } from '../utils/api-client'

declare global {
  interface Window {
    chmln?: {
      identify: (userId: string, userData: Record<string, unknown>) => void
      track: (eventName: string, eventData?: Record<string, unknown>) => void
      clear: () => void
    }
  }
}

interface ChameleonHashResponse {
  uid_hash: string
}

export const ChameleonProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth0()
  const { data: adminData, isSessionLoaded } = useCurrentUser()
  const initialized = useRef(false)
  const [uidHash, setUidHash] = useState<string | null>(null)

  // Load Chameleon script
  useEffect(() => {
    const chameleonToken = import.meta.env.VITE_CHAMELEON_TOKEN
    const chameleonEnabled = import.meta.env.VITE_CHAMELEON_ENABLED === 'true'

    if (!chameleonToken || !chameleonEnabled || initialized.current) {
      return
    }

    const w = window as any
    if (!w.chmln?.root) {
      w.chmln = w.chmln || {}
      w.chmln.accountToken = chameleonToken
      w.chmln.location = window.location.href.toString()
      w.chmln.now = new Date()
      w.chmln.fastUrl = 'https://fast.chameleon.io/'

      const methods = 'identify alias track clear set show on off custom help _data'.split(' ')
      for (const method of methods) {
        const queue: any[] = []
        w.chmln[method + '_a'] = queue
        w.chmln[method] = function () {
          queue.push(arguments)
        }
      }

      const script = document.createElement('script')
      script.src = `${w.chmln.fastUrl}messo/${chameleonToken}/messo.min.js`
      script.async = true
      document.head.appendChild(script)
    }

    initialized.current = true
  }, [])

  // Fetch HMAC hash when authenticated (skip license admins masquerading)
  useEffect(() => {
    const chameleonToken = import.meta.env.VITE_CHAMELEON_TOKEN
    const chameleonEnabled = import.meta.env.VITE_CHAMELEON_ENABLED === 'true'

    if (!chameleonToken || !chameleonEnabled) return
    if (!isSessionLoaded || !adminData) return

    // Skip for license admins (they're masquerading from portal)
    if (adminData.type === 'license_admin') return

    // Fetch the HMAC hash from the API. Background call — skip the expired-
    // session flow so an expected 401 can't trigger a re-login.
    apiClient.get<ChameleonHashResponse>('/api/chameleon/hash', undefined, undefined, true)
      .then(response => {
        if (response?.uid_hash) {
          setUidHash(response.uid_hash)
        }
      })
      .catch(error => {
        console.error('Failed to fetch Chameleon hash:', error)
      })
  }, [isSessionLoaded, adminData])

  // Identify user when we have both user data and hash
  useEffect(() => {
    const chameleonToken = import.meta.env.VITE_CHAMELEON_TOKEN
    const chameleonEnabled = import.meta.env.VITE_CHAMELEON_ENABLED === 'true'

    if (!chameleonToken || !chameleonEnabled) return
    if (!isSessionLoaded || !adminData) return

    // Skip for license admins (they're masquerading from portal)
    if (adminData.type === 'license_admin') return
    if (!uidHash) return

    window.chmln?.identify(String(adminData.id), {
      uid_hash: uidHash,
      admin_type: adminData.type,
      admin_level: adminData.level,
    })
  }, [isSessionLoaded, adminData, uidHash])

  return <>{children}</>
}
